import * as React from 'react';
import { SchoolAdminInterface, } from './interfaces'

import AdminRequestModal from './admin_request_modal'
import { DIAGNOSTIC_INSIGHTS_IMG_SRC, generateRecommendations, STARTER, INTERMEDIATE, ADVANCED, } from './shared'

import { requestGet, requestPost, } from '../../../../../modules/request'
import { PostNavigationBanner, Snackbar, defaultSnackbarTimeout, SMALL_ICON_BASE_SRC, LARGE_ICON_BASE_SRC, TEACHER, APPROVED, DENIED, PENDING, } from '../../../../Shared/index'
import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor'

const checkCircleImg = <img alt="Checked checkbox" src={`${SMALL_ICON_BASE_SRC}/check-circle.svg`} />
const checkCircleUncheckedImg = <img alt="Unchecked checkbox" src={`${SMALL_ICON_BASE_SRC}/check-circle-unchecked.svg`} />
const studentAccountsImg = <img alt="" src={`${LARGE_ICON_BASE_SRC}/student-accounts.svg`} />
const packsWholeImg = <img alt="" src={`${LARGE_ICON_BASE_SRC}/packs-whole.svg`} />
const activityPacksImg = <img alt="" src={`${LARGE_ICON_BASE_SRC}/activity-packs.svg`} />
const shannonHeadshotImg = <img alt="" src={`${DIAGNOSTIC_INSIGHTS_IMG_SRC}/shannon-headshot.png`} />
const productBezelImg = <img alt="" className="product-bezel" src={`${DIAGNOSTIC_INSIGHTS_IMG_SRC}/diagnostic-insights-product-bezel.svg`} />

interface LockedPageProps {
  needsEmailVerification: boolean;
  adminVerificationStatus: string;
  teacherVerificationStatus: string;
  role: string;
  schoolAdmins: SchoolAdminInterface[];
  alternativeSchoolNames: string[];
  school?: { name: string, };
}

const LockedPage = ({ school, needsEmailVerification, adminVerificationStatus, teacherVerificationStatus, role, schoolAdmins, alternativeSchoolNames, }: LockedPageProps) => {
  const [classCount, setClassCount] = React.useState<number>(null)
  const [recommendationsCount, setRecommendationsCount] = React.useState<number>(null)
  const [showAdminRequestModal, setShowAdminRequestModal] = React.useState<boolean>(false)
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarText, setSnackbarText] = React.useState<string>('');
  const [submittedVerificationRequest, setSubmittedVerificationRequest] = React.useState<boolean>(false)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  React.useEffect(() => {
    getCompleteHistory()
  }, [])

  function getCompleteHistory() {
    requestGet('/diagnostic_insights/complete_history', (body) => {
      let classCount = 0
      let recommendationsCount = 0
      Object.keys(body).forEach((classroomId) => {
        const classroom = body[classroomId]
        const ellLearningSequences = classroom.ell.filter(student => Object.keys(student.student_learning_sequences).length)
        const nonELLLearningSequences = classroom.non_ell.filter(student => Object.keys(student.student_learning_sequences).length)

        const ellRecommendations = generateRecommendations(true, ellLearningSequences, classroomId)
        const nonELLRecommendations = generateRecommendations(false, nonELLLearningSequences, classroomId)

        const ellRecommendationsCount = [STARTER, INTERMEDIATE, ADVANCED].reduce((sum, diagnosticLevel) => {
          return sum + ellRecommendations[diagnosticLevel].student_ids.length
        }, 0)

        const nonELLRecommendationsCount = [STARTER, INTERMEDIATE, ADVANCED].reduce((sum, diagnosticLevel) => {
          return sum + nonELLRecommendations[diagnosticLevel].student_ids.length
        }, 0)

        if (ellRecommendationsCount || nonELLRecommendationsCount) {
          classCount += 1
          recommendationsCount += ellRecommendationsCount + nonELLRecommendationsCount
        }

        setClassCount(classCount)
        setRecommendationsCount(recommendationsCount)
      })
    })
  }

  function openAdminRequestModal() { setShowAdminRequestModal(true) }
  function closeAdminRequestModal() { setShowAdminRequestModal(false) }

  function onAdminRequestSuccess(snackbarCopy) {
    closeAdminRequestModal()
    setSubmittedVerificationRequest(true)
    setSnackbarText(snackbarCopy)
    setShowSnackbar(true)
  }

  function verifyEmail() {
    requestPost(`${window.postbuildEnv.DEFAULT_URL}/verify_emails/require_email_verification`, { email_redirect_url: window.location.href }, (body) => {
      if (body.redirect) {
        window.location.href = body.redirect
      } else {
        window.location.reload()
      }
    })
  }

  const renderRightSide = () => {
    const schoolCheckbox = !school || alternativeSchoolNames.includes(school.name) ? checkCircleUncheckedImg : checkCircleImg
    const emailVerificationCheckbox = needsEmailVerification ? checkCircleUncheckedImg : checkCircleImg
    const teacherVerificationCheckbox = teacherVerificationStatus !== APPROVED ? checkCircleUncheckedImg : checkCircleImg

    return (
      <div className="checklist">
        <div className="checklist-item">{schoolCheckbox}Select your school</div>
        <div className="checklist-item">{emailVerificationCheckbox}Verify your email</div>
        <div className="checklist-item">{teacherVerificationCheckbox}Get verified by an admin</div>
      </div>
    )
  }

  const renderTeacherBanner = () => {
    let heading
    let copy
    let button

    if (!school || alternativeSchoolNames.includes(school.name)) {
      heading = 'Action required'
      copy = <React.Fragment>Please select your school to use this feature. If your school is not listed please <a href="mailto:hello@quill.org" rel="noopener noreferrer" target="_blank">contact us</a>.</React.Fragment>
      button = {
        href: '/teachers/my_account',
        text: 'Select school'
      }
    } else if (needsEmailVerification) {
      heading = 'Action required'
      copy = 'Please verify your email to use this feature'
      button = {
        onClick: verifyEmail,
        text: 'Verify your email'
      }
    } else if (schoolAdmins.length === 0) {
      heading = 'Action required'
      copy = `${school.name} does not have an admin. It's free and takes just a minute to invite one.`
      button = {
        href: '/admin_access',
        text: 'Invite an admin'
      }
    } else if (teacherVerificationStatus === PENDING || submittedVerificationRequest) {
      heading = 'Your request has been sent'
      copy = 'Your verification request is pending review. Once verified, you will receive an email letting you know that you have access to this feature. If you need help in the meantime, contact an admin.'
      button = {
        href: '/admin_access',
        text: 'Contact an admin'
      }
    } else if (teacherVerificationStatus === DENIED) {
      heading = 'Your admin couldn’t verify you'
      copy = `Sorry, your admin couldn’t verify you as a teacher at ${school.name}. If you need help, contact us.`
      button = {
        href: 'mailto:hello@quill.org',
        text: 'Contact us',
        target: '_blank'
      }
    } else if (!teacherVerificationStatus) {
      heading = 'Action required'
      copy = `You need to be verified by an admin as a teacher at ${school.name} to use this feature`
      button = {
        onClick: openAdminRequestModal,
        text: 'Request verification'
      }
    }

    return (
      <PostNavigationBanner
        bannerColor="grey"
        bannerStyle="light-mode"
        bodyText={copy}
        buttons={[button]}
        primaryHeaderText={heading}
        rightSide={renderRightSide()}
      />
    )
  }

  const renderAdminBanner = () => {
    let heading = 'Action required'
    let copy = 'Please complete the verification process to use this feature.'
    let button = {
      href: '/teachers/premium_hub',
      text: 'Go to Premium Hub'
    }

    switch(adminVerificationStatus) {
      case PENDING:
        heading = 'We’re reviewing your request'
        copy = 'Your verification request is pending approval. Once approved, you will be able to use this feature. If you need help in the meantime, contact us.'
        button = {
          href: 'mailto:hello@quill.org',
          text: 'Contact us',
          target: '_blank'
        }
        break
      case DENIED:
        heading = 'We couldn’t verify you'
        copy = `Sorry, we couldn’t verify you as an admin of ${school.name}. This means you can't access this feature. If you need help, contact us.`
        button = {
          href: 'mailto:hello@quill.org',
          text: 'Contact us',
          target: '_blank'
        }
        break
    }

    return (
      <PostNavigationBanner
        bannerColor="grey"
        bannerStyle="light-mode"
        bodyText={copy}
        buttons={[button]}
        primaryHeaderText={heading}
      />
    )
  }

  return (
    <div className="diagnostic-insights-locked-page container">
      {showAdminRequestModal && (
        <AdminRequestModal
          closeModal={closeAdminRequestModal}
          onSuccess={onAdminRequestSuccess}
          schoolAdmins={schoolAdmins}
          schoolName={school.name}
        />
      )}
      <Snackbar text={snackbarText} visible={showSnackbar} />
      {role === TEACHER ? renderTeacherBanner() : renderAdminBanner()}
      <header>
        <span className="new-tag">NEW</span>
        <h1>Diagnostic Insights</h1>
        <p className="sub-header">Make smarter diagnostic decisions with comprehensive student history and personalized recommendations. Free for verified teachers.</p>
      </header>
      <section className="call-outs">
        {!!(classCount && recommendationsCount) && (
          <div className="recommendations-available">
            <p>Recommendations available for <b>{recommendationsCount} student{recommendationsCount === 1 ? '' : 's'}</b> in <b>{classCount} of your classes</b>.</p>
          </div>
        )}
        <div>
          <b>Not verified yet?</b> You can still assign diagnostics and view reports for those you’ve assigned this school year. <a href="https://support.quill.org/en/articles/10751323-how-do-teachers-use-diagnostic-insights" rel="noopener noreferrer" target="_blank">Learn more</a>
        </div>
      </section>
      {productBezelImg}
      <h2>Why Teachers Love This Report</h2>
      <section className="reasons">
        <div className="reason">
          {studentAccountsImg}
          <h3>Smart Recommendations</h3>
          <p>Get intelligent guidance on which diagnostic to assign next, based on each student's complete diagnostic history and demonstrated proficiency levels.</p>
        </div>
        <div className="reason">
          {packsWholeImg}
          <h3>Complete Performance Overview</h3>
          <p>See at a glance how your class is performing across all diagnostic skills, with clear indicators showing proficiency levels and areas needing focus.</p>
        </div>
        <div className="reason">
          {activityPacksImg}
          <h3>Historical Insights</h3>
          <p>Access a complete view of every student's diagnostic journey - including results from previous teachers and classes - all in one unified dashboard.</p>
        </div>
      </section>
      <section className="testimonial">
        {shannonHeadshotImg}
        <p className="quote">“Diagnostic Insights empowers teachers with the information they need from day one. Now, teachers can instantly see each student's complete diagnostic history and get smart recommendations for what to assign next. It eliminates the redundancy and guesswork, letting teachers focus on what matters - supporting student growth.”</p>
        <p className="attribution">Shannon Browne, Professional Learning Manager</p>
      </section>
    </div>
  )
}

export default LockedPage
